import React, { useMemo, useState, useEffect } from 'react';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import ReviewModal from '../components/ReviewModal';
import CancelAppointmentModal from '../components/CancelAppointmentModal';
import { LoadingList } from '../components/LoadingComponents';
import { useAppContext } from '../context/AppContext';
import { createReview, hasUserReviewed } from '../services/reviewService';
import { cancelAppointmentWithReason, canCancelAppointment } from '../services/appointmentService';
import { createNotification } from '../services/notificationService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

/**
 * Página: MyAppointmentsPage (/pages/MyAppointmensPage.jsx)
 * Página de agendamentos do usuário
 */
const MyAppointmentsPage = () => {
  const { currentUser, appointments, barbershops, updateAppointmentStatus, loading } = useAppContext();
  const navigate = useNavigate();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [reviewedAppointments, setReviewedAppointments] = useState({});
  const [cancelValidation, setCancelValidation] = useState({ canCancel: true, hoursUntilAppointment: 0 });

  // Redirecionar usuários não logados
  useEffect(() => {
    if (!currentUser) {
      toast.error('Você precisa fazer login para ver seus agendamentos');
      navigate('/login', { state: { from: '/meus-agendamentos' } });
    }
  }, [currentUser, navigate]);

  // Verificar quais agendamentos já foram avaliados
  useEffect(() => {
    if (!currentUser) return;
    const checkReviews = async () => {
      const completedApps = appointments.filter(
        a => a.userId === currentUser.id && a.status === 'completed'
      );
      
      const reviewStatus = {};
      for (const app of completedApps) {
        const result = await hasUserReviewed(currentUser.id, app.id);
        if (result.success) {
          reviewStatus[app.id] = result.data;
        }
      }
      setReviewedAppointments(reviewStatus);
    };

    if (currentUser?.id) {
      checkReviews();
    }
  }, [appointments, currentUser]);

  const userAppointments = useMemo(() => {
    if (!currentUser) return [];
    
    return appointments
      .filter(a => a.userId === currentUser.id)
      .map(app => ({
        ...app,
        barbershopName: barbershops.find(b => b.id === app.barbershopId)?.name || 'Barbearia Excluída',
        dateTime: new Date(app.date + 'T' + app.time),
      }))
      .sort((a, b) => b.dateTime - a.dateTime); // Mais recente primeiro
  }, [appointments, currentUser, barbershops]);

  const now = new Date();
  const upcoming = userAppointments.filter(a => a.dateTime >= now && (a.status === 'confirmed' || a.status === 'pending'));
  const history = userAppointments.filter(a => a.dateTime < now || a.status === 'completed' || a.status === 'cancelled');

  // Return early se não tiver usuário
  if (!currentUser) {
    return null;
  }

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pendente</span>;
      case 'confirmed':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Confirmado</span>;
      case 'completed':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Concluído</span>;
      case 'cancelled':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Cancelado</span>;
      default:
        return null;
    }
  };

  const handleOpenReviewModal = (app) => {
    setSelectedAppointment(app);
    setIsReviewModalOpen(true);
  };

  const handleOpenCancelModal = (app) => {
    setSelectedAppointment(app);
    const validation = canCancelAppointment(app.date, app.time);
    setCancelValidation(validation);
    setIsCancelModalOpen(true);
  };

  const handleCancelAppointment = async (reason) => {
    if (!selectedAppointment) return;

    const result = await cancelAppointmentWithReason(selectedAppointment.id, reason);

    if (result.success) {
      // Atualizar o status no contexto
      updateAppointmentStatus(selectedAppointment.id, 'cancelled');

      // Notificar o dono da barbearia
      const barbershop = barbershops.find(b => b.id === selectedAppointment.barbershopId);
      if (barbershop?.ownerId) {
        await createNotification({
          userId: barbershop.ownerId,
          type: 'appointment_cancelled',
          title: 'Agendamento Cancelado',
          message: `${currentUser.name} cancelou o agendamento de ${selectedAppointment.service} para ${new Date(selectedAppointment.date + 'T' + selectedAppointment.time).toLocaleDateString('pt-BR')} às ${selectedAppointment.time}.${reason ? ` Motivo: ${reason}` : ''}`,
          relatedId: selectedAppointment.id
        });
      }

      toast.success('Agendamento cancelado com sucesso!');
      setIsCancelModalOpen(false);
      setSelectedAppointment(null);
    } else {
      toast.error('Erro ao cancelar agendamento. Tente novamente.');
    }
  };

  const handleSubmitReview = async (reviewData) => {
    const barbershop = barbershops.find(b => b.id === selectedAppointment.barbershopId);
    
    const result = await createReview({
      ...reviewData,
      userId: currentUser.id,
      userName: currentUser.name,
      barbershopId: selectedAppointment.barbershopId,
      barbershopName: barbershop?.name || 'Barbearia',
      appointmentId: selectedAppointment.id,
    });

    if (result.success) {
      toast.success('Avaliação enviada com sucesso!');
      setReviewedAppointments(prev => ({
        ...prev,
        [selectedAppointment.id]: true
      }));
    } else {
      toast.error('Erro ao enviar avaliação');
    }
  };

  const AppointmentCard = ({ app }) => (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
      <div className="flex flex-col sm:flex-row sm:justify-between">
        <div>
          <h3 className="text-xl font-bold text-indigo-700">{app.barbershopName}</h3>
          <p className="text-gray-700 font-semibold mt-1">{app.service}</p>
        </div>
        <div className="mt-2 sm:mt-0 sm:text-right">
          {getStatusChip(app.status)}
        </div>
      </div>
      <div className="border-t border-gray-100 my-4"></div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-gray-600">
        <div className="flex items-center mb-2 sm:mb-0">
          {icons.calendar({ className: 'h-5 w-5 mr-2' })}
          <span>{app.dateTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        </div>
        <div className="flex items-center mb-2 sm:mb-0">
          {icons.clock({ className: 'h-5 w-5 mr-2' })}
          <span>{app.time}</span>
        </div>
        <div className="flex gap-2 mt-3 sm:mt-0">
          {(app.status === 'pending' || app.status === 'confirmed') && (
            <Button variant="danger" className="py-1 px-3 text-sm" onClick={() => handleOpenCancelModal(app)}>
              {icons.xCircle({ className: 'h-4 w-4 mr-1 inline' })}
              Cancelar
            </Button>
          )}
          {app.status === 'completed' && !reviewedAppointments[app.id] && (
            <Button variant="primary" className="py-1 px-3 text-sm" onClick={() => handleOpenReviewModal(app)}>
              {icons.star({ className: 'h-4 w-4 mr-1 inline' })}
              Avaliar
            </Button>
          )}
          {app.status === 'completed' && reviewedAppointments[app.id] && (
            <span className="text-sm text-green-600 font-semibold flex items-center">
              {icons.check({ className: 'h-4 w-4 mr-1' })}
              Avaliado
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-10">Meus Agendamentos</h1>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Próximos Agendamentos</h2>
          {loading ? (
            <LoadingList count={2} />
          ) : upcoming.length > 0 ? (
            <div className="space-y-6">
              {upcoming.map(app => <AppointmentCard key={app.id} app={app} />)}
            </div>
          ) : (
            <p className="text-gray-500">Você não possui agendamentos futuros.</p>
          )}
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Histórico</h2>
          {loading ? (
            <LoadingList count={3} />
          ) : history.length > 0 ? (
            <div className="space-y-6">
              {history.map(app => <AppointmentCard key={app.id} app={app} />)}
            </div>
          ) : (
            <p className="text-gray-500">Seu histórico de agendamentos está vazio.</p>
          )}
        </section>
      </div>

      {/* Modal de Avaliação */}
      {selectedAppointment && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          barbershopName={selectedAppointment.barbershopName}
          appointmentId={selectedAppointment.id}
          onSubmit={handleSubmitReview}
        />
      )}

      {/* Modal de Cancelamento */}
      <CancelAppointmentModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        appointment={selectedAppointment}
        onConfirm={handleCancelAppointment}
        canCancel={cancelValidation.canCancel}
        hoursUntilAppointment={cancelValidation.hoursUntilAppointment}
      />
    </div>
  );
};

export default MyAppointmentsPage;
